import { PropsWithChildren, useEffect } from 'react';
import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';

/**
 * Howden digital brand palette.
 *
 * Note: The style guide OCR shows Rosewood as "#3cOf11";
 * this should be "#3c0f11" with a zero.
 */
export const howden = {
  navy: '#071a33',
  navyMid: '#102d52',
  blueGrey: '#9fb3c8',
  cobaltBlue: '#0857c3',
  pistachio: '#e0ec89',
  teal: '#7ce0d3',
  mossGreen: '#173f35',
  gold: '#8d741e',
  rosewood: '#3c0f11',
  mustard: '#ffbf3f',
  poppy: '#f9423a',

  grey: '#3d3d3d',
  grey60: '#8b8b8b',
  grey10: '#ececec',
  grey5: '#f5f5f5',
  white: '#ffffff',
  black: '#000000',
};

const headingFontFamily = '"GT Ultra Median", Georgia, serif';
const bodyFontFamily = '"Aktiv Grotesk", Arial, sans-serif';

export const howdenLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,

      primary: {
        main: howden.mossGreen,
        light: howden.pistachio,
        dark: howden.rosewood,
        contrastText: howden.white,
      },

      secondary: {
        main: howden.cobaltBlue,
        light: howden.teal,
        dark: howden.mossGreen,
        contrastText: howden.white,
      },

      error: {
        main: howden.poppy,
      },

      warning: {
        main: howden.mustard,
      },

      info: {
        main: howden.cobaltBlue,
      },

      success: {
        main: howden.mossGreen,
      },

      background: {
        default: howden.white,
        paper: howden.grey5,
      },

      text: {
        primary: howden.grey,
        secondary: howden.grey60,
      },

      navigation: {
        background: howden.navy,
        indicator: howden.pistachio,
        color: howden.white,
        selectedColor: howden.pistachio,
      },
    },
  }),

  fontFamily: bodyFontFamily,

  defaultPageTheme: 'home',

  pageTheme: {
    home: genPageTheme({
      colors: [howden.mossGreen, howden.pistachio],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [howden.cobaltBlue, howden.teal],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [howden.mossGreen, howden.pistachio],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [howden.cobaltBlue, howden.teal],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [howden.gold, howden.mustard],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [howden.mossGreen, howden.pistachio],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [howden.grey, howden.grey10],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [howden.mossGreen, howden.pistachio],
      shape: shapes.round,
    }),
    apis: genPageTheme({
      colors: [howden.cobaltBlue, howden.teal],
      shape: shapes.wave,
    }),
  },

  typography: {
    htmlFontSize: 16,
    fontFamily: bodyFontFamily,

    h1: {
      fontFamily: headingFontFamily,
      fontSize: 60,
      fontWeight: 500,
      marginBottom: 0,
    },
    h2: {
      fontFamily: headingFontFamily,
      fontSize: 44,
      fontWeight: 500,
      marginBottom: 0,
    },
    h3: {
      fontFamily: headingFontFamily,
      fontSize: 32,
      fontWeight: 500,
      marginBottom: 0,
    },
    h4: {
      fontFamily: headingFontFamily,
      fontSize: 24,
      fontWeight: 500,
      marginBottom: 0,
    },
    h5: {
      fontFamily: headingFontFamily,
      fontSize: 20,
      fontWeight: 500,
      marginBottom: 0,
    },
    h6: {
      fontFamily: bodyFontFamily,
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 0,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: howden.white,
          color: howden.grey,
          fontFamily: bodyFontFamily,
        },
        'h1, h2, h3, h4, h5': {
          fontFamily: headingFontFamily,
        },
        a: {
          color: howden.cobaltBlue,
          textDecorationColor: howden.teal,
        },
        'a:hover': {
          color: howden.mossGreen,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          minHeight: 40,
          padding: '8px 16px',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: howden.mossGreen,
          color: howden.white,
          '&:hover': {
            backgroundColor: howden.pistachio,
            color: howden.mossGreen,
          },
        },
        containedSecondary: {
          backgroundColor: howden.cobaltBlue,
          color: howden.white,
          '&:hover': {
            backgroundColor: howden.teal,
            color: howden.mossGreen,
          },
        },
        outlinedPrimary: {
          borderColor: howden.mossGreen,
          color: howden.mossGreen,
          '&:hover': {
            borderColor: howden.mossGreen,
            backgroundColor: howden.pistachio,
          },
        },
        textPrimary: {
          color: howden.mossGreen,
          '&:hover': {
            backgroundColor: howden.grey5,
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 2,
        },
        elevation1: {
          boxShadow: 'none',
          border: `1px solid ${howden.grey10}`,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          border: `1px solid ${howden.grey10}`,
          boxShadow: 'none',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          fontFamily: bodyFontFamily,
        },
        colorPrimary: {
          backgroundColor: howden.pistachio,
          color: howden.mossGreen,
        },
        colorSecondary: {
          backgroundColor: howden.teal,
          color: howden.mossGreen,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          color: howden.grey,
          backgroundColor: howden.grey5,
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        body1: {
          fontFamily: bodyFontFamily,
          fontSize: 16,
          lineHeight: 1.5,
        },
        body2: {
          fontFamily: bodyFontFamily,
          fontSize: 14,
          lineHeight: 1.5,
        },
      },
    },
  },
});

/**
 * Adds a body class while the Howden theme is active.
 * This lets the BUI CSS target Howden specifically, rather than every light theme.
 */
export function HowdenThemeProvider({ children }: PropsWithChildren<{}>) {
  useEffect(() => {
    document.body.classList.add('theme-howden');

    return () => {
      document.body.classList.remove('theme-howden');
    };
  }, []);

  return (
    <UnifiedThemeProvider theme={howdenLightTheme}>
      {children}
    </UnifiedThemeProvider>
  );
}
